/**
 * Online booking — public page (`/online-booking`).
 *
 * 6-step wizard:
 *   1. Pick an industry      (organization-type/get)
 *   2. Pick an organization + branch (organizations/get + organization-type filter)
 *   3. Pick a service        (categories tree)
 *   4. Pick a time           (get_mobile_order_time_suggestions/{branch}/{cat})
 *   5. Confirmation          (mobile-ticket/create)
 *
 * The page itself owns all wizard state — every step is a presentational
 * component that takes data + selection callbacks. Because the booking
 * flow is anonymous-first we don't gate on authentication; logged-in users
 * reuse their account contact data instead of retyping it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import useAuth from "../helper/useAuth";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import {
  cancelTicket,
  createTicket,
  fetchBranchDetail,
  fetchBranchWorkers,
  fetchCategoriesByUser,
  fetchIndustries,
  fetchOrganizationDetail,
  fetchOrganizations,
  fetchTimeSuggestions,
  fetchUsersByCategory,
  armenianToLatin,
  getServiceCategoryId,
  getServiceUserId,
  setWorkerRating,
} from "../helper/bookingApi";
import { useTheme } from "../components/ThemeProvider";

import { getBookingPalette } from "../components/booking/BookingTheme";
import { BookingProgress } from "../components/booking/BookingProgress";
import { BOOKING_FONT_FAMILY } from "../components/booking/BookingPrimitives";
import {
  BookingEntryChoiceStep,
  IndustryStep,
  OrganizationStep,
  SpecialistSelectionStep,
} from "../components/booking/BookingSteps";
import { ServiceStep } from "../components/booking/BookingServiceStep";
import { TimeSlotStep } from "../components/booking/BookingTimeStep";
import { ConfirmStep } from "../components/booking/BookingConfirmStep";
import { TicketSuccessModal } from "../components/booking/TicketSuccessModal";
import SalonBookingPage from "./SalonBookingPage";

const STEP_INDUSTRY = 0;
const STEP_ORG = 1;
const STEP_SERVICE = 2;
const STEP_TIME = 3;
const STEP_CONFIRM = 4;
const TOTAL_STEPS = 5;
const ENTRY_SERVICE = "service";
const ENTRY_SPECIALIST = "specialist";
const SERVICE_SUB_WORKERS = "workers";
const SERVICE_SUB_SERVICES = "services";
const DEFAULT_PROGRESS_STEPS = [
  "industry",
  "organization",
  "service",
  "datetime",
  "confirm",
];
const ENTRY_PROGRESS_STEPS = [
  "industry",
  "organization",
  "entry",
  "specialist",
  "service",
  "datetime",
  "confirm",
];
const ENTRY_SERVICE_FIRST_PROGRESS_STEPS = [
  "industry",
  "organization",
  "entry",
  "service",
  "specialist",
  "datetime",
  "confirm",
];

const URL_STEP_KEYS = {
  industry: STEP_INDUSTRY,
  organization: STEP_ORG,
  branch: STEP_SERVICE,
  entry: STEP_SERVICE,
  service: STEP_SERVICE,
  time: STEP_TIME,
  confirm: STEP_CONFIRM,
};

const STEP_URL_KEYS = {
  [STEP_INDUSTRY]: "industry",
  [STEP_ORG]: "organization",
  [STEP_SERVICE]: "service",
  [STEP_TIME]: "time",
  [STEP_CONFIRM]: "confirm",
};

function branchNeedsEntryChoice(branch) {
  return Number(branch?.display_type) === 1;
}

function isMultiBookingEnabled(branch) {


  return (
    branch?.multi_booking === true ||
    branch?.multi_booking === 1 ||
    branch?.multi_booking === "1" ||
    branch?.multi_booking === "true"
  );
}

function getSelectedServices(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function getPrimaryService(value) {
  return getSelectedServices(value)[0] || null;
}

function hasMultipleSelectedServices(value) {
  return getSelectedServices(value).length > 1;
}

function getSelectedCategoryIds(value) {
  return getSelectedServices(value)
    .map((item) => item?.categoryId ?? getServiceCategoryId(item))
    .filter((id) => id != null);
}

function firstFilled(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
}

function getUserContact(user) {
  const firstName = firstFilled(user?.first_name, user?.firstName);
  const lastName = firstFilled(user?.last_name, user?.lastName);
  const joinedName = [firstName, lastName].filter(Boolean).join(" ");

  return {
    name: firstFilled(
      user?.name,
      user?.full_name,
      user?.fullName,
      joinedName,
      user?.username,
    ),
    email: firstFilled(user?.email, user?.mail),
    phone: firstFilled(
      user?.phone,
      user?.phone_number,
      user?.phoneNumber,
      user?.mobile,
      user?.mobile_phone,
    ),
  };
}

function withBookingQuery(
  path,
  {
    stepKey = null,
    industryItem = null,
    mode = null,
    subStep = null,
    categoryItem = null,
    workerItem = null,
  } = {},
) {
  const params = new URLSearchParams();
  if (stepKey) params.set("step", stepKey);
  if (mode) params.set("mode", mode);
  if (subStep) params.set("sub", subStep);
  if (categoryItem?.id) {
    const categoryName =
      categoryItem.urlName ||
      categoryItem.name ||
      categoryItem.translate?.name ||
      categoryItem.translates?.[0]?.name ||
      categoryItem.id;
    params.set("category", makeQueryToken(categoryItem.id, categoryName));
  }
  if (workerItem?.id) {
    const workerName =
      workerItem.urlName ||
      [workerItem.name, workerItem.surname].filter(Boolean).join(" ") ||
      workerItem.name ||
      workerItem.id;
    params.set("worker", makeQueryToken(workerItem.id, workerName));
  }
  if (industryItem?.id) {
    const industryName =
      industryItem.urlName ||
      industryItem.name ||
      industryItem.translate?.name ||
      industryItem.translates?.[0]?.name ||
      industryItem.id;
    params.set("industry", makeQueryToken(industryItem.id, industryName));
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function makeUrlToken(id, label) {
  if (id == null) return "";
  const safeLabel = armenianToLatin(String(label || ""))
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${encodeURIComponent(safeLabel || id)}`;
}

function makeQueryToken(id, label) {
  if (id == null) return "";
  const safeLabel = armenianToLatin(String(label || ""))
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${safeLabel || id}`;
}

function parseUrlToken(token) {
  if (!token) return null;
  const [idPart, ...nameParts] = String(token).split(",");
  const id = Number(idPart);
  if (!idPart || Number.isNaN(id)) return null;
  const rawName = nameParts.join(",");
  let urlName = rawName || "";
  try {
    urlName = decodeURIComponent(rawName);
  } catch (_err) {
    urlName = rawName;
  }
  return { id, name: urlName.replace(/-/g, " ").trim(), urlName: urlName.trim() };
}

function getOrganizationFromDetail(detail) {
  if (detail?.organization) return detail.organization;
  if (detail?.organisation) return detail.organisation;
  if (detail && !Array.isArray(detail) && detail.id) return detail;
  return null;
}

function normalizeWorker(worker) {
  if (!worker) return worker;
  const professions = Array.isArray(worker.professions)
    ? worker.professions
    : Array.isArray(worker.proffesion)
      ? worker.proffesion
      : Array.isArray(worker.profession)
        ? worker.profession
        : null;

  return professions ? { ...worker, professions } : worker;
}

function hasProfessions(worker) {
  return Array.isArray(worker?.professions) && worker.professions.length > 0;
}

function getServiceProfession(service) {
  const selectedServices = getSelectedServices(service);
  for (const item of selectedServices) {
    const candidates = [
      item?.profession,
      item?.proffesion,
      item?.professions,
      item?.props?.profession,
      item?.props?.proffesion,
      item?.props?.professions,
      item?.category?.profession,
      item?.category?.proffesion,
      item?.category?.professions,
      item?.category?.props?.profession,
      item?.category?.props?.proffesion,
      item?.category?.props?.professions,
    ];
    const found = candidates.find((value) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value),
    );
    if (found) return found;
  }
  return null;
}

const BookingPage = ({
  setOpenRegisterModal,
  setOpenLoginModal,
  setLoginModalTitle,
} = {}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { bookingIndustry, bookingOrg, bookingBranch } = useParams();
  const { isAuthenticated } = useAuth();
  const { isDarkMode } = useTheme();
  const isVisuallyDark = !isDarkMode;
  const palette = useMemo(
    () => getBookingPalette(isVisuallyDark),
    [isVisuallyDark],
  );

  const [step, setStep] = useState(STEP_INDUSTRY);
  // Tracks whether the most recent step transition was forward or backward,
  // so the branch auto-skip can preserve user intent (skip in the same
  // direction the user was already navigating).
  const lastDirectionRef = useRef(1);
  const bookingScrollTargetRef = useRef(null);
  const didMountScrollRef = useRef(false);

  // Selections
  const [industry, setIndustry] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [branch, setBranch] = useState(null);
  const [service, setService] = useState(null);
  const [slot, setSlot] = useState(null);
  const [entryMode, setEntryMode] = useState(ENTRY_SERVICE);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerServicesOpen, setWorkerServicesOpen] = useState(false);
  const [categoryWorkersOpen, setCategoryWorkersOpen] = useState(false);

  // User-entered data (Step 5). Phone is collected for future email/SMS
  // confirmation flows — currently the backend doesn't accept these fields,
  // so they only live on the frontend until that lands.
  const [email, setEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [phone, setPhone] = useState("");
  const [clientComment, setClientComment] = useState("");

  // Auth-fetched user (only when logged in, for email pre-fill)
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const accountContact = useMemo(() => getUserContact(user), [user]);
  const selectedWorkerProfession = useMemo(
    () => getServiceProfession(service),
    [service],
  );
  const effectiveClientName = firstFilled(clientName, accountContact.name);
  const effectiveEmail = firstFilled(email, accountContact.email);
  const effectivePhone = firstFilled(phone, accountContact.phone);

  // Data caches
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(true);
  const [industriesError, setIndustriesError] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsError, setOrganizationsError] = useState(null);

  const [branches, setBranches] = useState([]);
  const [, setBranchesLoading] = useState(false);
  const [, setBranchesError] = useState(null);

  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workersError, setWorkersError] = useState(null);

  // Ticket-create state
  const [ticketState, setTicketState] = useState("idle"); // idle | creating | success | error
  const [ticket, setTicket] = useState(null);
  const [ticketError, setTicketError] = useState(null);
  // Success modal opens automatically the first time ticketState flips
  // to "success"; closing the modal reveals the ConfirmStep summary
  // beneath it, where the same actions remain available.
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const resetEntryBranches = useCallback(() => {
    setSelectedWorker(null);
    setWorkerServicesOpen(false);
    setCategoryWorkersOpen(false);
  }, []);

  const industryFromUrl = useMemo(
    () => parseUrlToken(bookingIndustry),
    [bookingIndustry],
  );
  const industryFromQuery = useMemo(
    () => parseUrlToken(searchParams.get("industry")),
    [searchParams],
  );
  const selectedIndustryFromUrl = industryFromUrl || industryFromQuery;
  const orgFromUrl = useMemo(
    () => parseUrlToken(bookingOrg || searchParams.get("org")),
    [bookingOrg, searchParams],
  );
  const branchFromUrl = useMemo(
    () => parseUrlToken(bookingBranch || searchParams.get("branch")),
    [bookingBranch, searchParams],
  );
  const rescheduleOrderId = useMemo(() => {
    const value = Number(searchParams.get("reschedule"));
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [searchParams]);
  const requestedStep = useMemo(() => {
    const key = searchParams.get("step");
    return key && key in URL_STEP_KEYS ? URL_STEP_KEYS[key] : null;
  }, [searchParams]);
  const requestedEntryMode = useMemo(() => {
    const mode = searchParams.get("mode");
    return mode === ENTRY_SERVICE || mode === ENTRY_SPECIALIST ? mode : null;
  }, [searchParams]);
  const requestedServiceSubStep = useMemo(() => {
    const sub = searchParams.get("sub");
    return sub === SERVICE_SUB_WORKERS || sub === SERVICE_SUB_SERVICES
      ? sub
      : null;
  }, [searchParams]);
  const requestedCategoryFromUrl = useMemo(
    () => parseUrlToken(searchParams.get("category")),
    [searchParams],
  );
  const requestedWorkerFromUrl = useMemo(
    () => parseUrlToken(searchParams.get("worker")),
    [searchParams],
  );

  const bookingBasePath = useMemo(
    () => (lang === "en" ? "/en/online-booking" : "/online-booking"),
    [lang],
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle(
      "booking-hide-site-footer",
      step >= STEP_ORG,
    );
    document.body.classList.toggle(
      "booking-confirm-step",
      step === STEP_CONFIRM,
    );
    return () => {
      document.body.classList.remove("booking-hide-site-footer");
      document.body.classList.remove("booking-confirm-step");
    };
  }, [step]);

  const navigatePreservingScroll = useCallback(
    (to, options = {}) => {
      const scrollX = typeof window !== "undefined" ? window.scrollX : 0;
      const scrollY = typeof window !== "undefined" ? window.scrollY : 0;
      navigate(to, { ...options, preventScrollReset: true });
      if (typeof window === "undefined") return;

      window.requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
        window.setTimeout(() => window.scrollTo(scrollX, scrollY), 80);
      });
    },
    [navigate],
  );

  const syncBookingUrl = useCallback(
    (
      nextOrg,
      nextBranch = null,
      {
        replace = false,
        stepKey = null,
        mode = null,
        subStep = null,
        categoryItem = null,
        workerItem = null,
        preserveScroll = true,
      } = {},
    ) => {
      const navigateUrl = preserveScroll ? navigatePreservingScroll : navigate;
      const navigateOptions = { replace, preventScrollReset: true };
      if (!nextOrg?.id) {
        navigateUrl(
          withBookingQuery(bookingBasePath, {
            stepKey,
            industryItem: industry,
            mode,
            subStep,
            categoryItem,
            workerItem,
          }),
          navigateOptions,
        );
        return;
      }
      const orgName =
        nextOrg.urlName ||
        nextOrg.name ||
        nextOrg.translate?.name ||
        nextOrg.translates?.[0]?.name ||
        nextOrg.id;
      let nextPath = `${bookingBasePath}/${makeUrlToken(nextOrg.id, orgName)}`;
      if (nextBranch?.id) {
        const branchName =
          nextBranch.urlName ||
          nextBranch.name ||
          nextBranch.translate?.name ||
          nextBranch.translates?.[0]?.name ||
          nextBranch.id;
        nextPath += `/${makeUrlToken(nextBranch.id, branchName)}`;
      }
      navigateUrl(
        withBookingQuery(nextPath, {
          stepKey,
          industryItem: industry,
          mode,
          subStep,
          categoryItem,
          workerItem,
        }),
        navigateOptions,
      );
    },
    [bookingBasePath, industry, navigate, navigatePreservingScroll],
  );

  const syncIndustryUrl = useCallback(
    (
      nextIndustry,
      { replace = false, stepKey = null, preserveScroll = true } = {},
    ) => {
      const navigateUrl = preserveScroll ? navigatePreservingScroll : navigate;
      const navigateOptions = { replace, preventScrollReset: true };
      if (!nextIndustry?.id) {
        navigateUrl(
          withBookingQuery(bookingBasePath, { stepKey }),
          navigateOptions,
        );
        return;
      }
      const industryName =
        nextIndustry.urlName ||
        nextIndustry.name ||
        nextIndustry.translate?.name ||
        nextIndustry.translates?.[0]?.name ||
        nextIndustry.id;
      navigateUrl(
        withBookingQuery(
          `${bookingBasePath}/industry/${makeUrlToken(nextIndustry.id, industryName)}`,
          { stepKey },
        ),
        navigateOptions,
      );
    },
    [bookingBasePath, navigate, navigatePreservingScroll],
  );

  /* -------------------- Industries -------------------- */
  const loadIndustries = useCallback(async () => {
    setIndustriesLoading(true);
    setIndustriesError(null);
    try {
      const data = await fetchIndustries(lang);
      setIndustries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[booking] industries load failed", err);
      setIndustriesError(err);
    } finally {
      setIndustriesLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    loadIndustries();
  }, [loadIndustries]);

  useEffect(() => {
    if (
      selectedIndustryFromUrl?.id ||
      orgFromUrl?.id ||
      branchFromUrl?.id ||
      requestedStep != null
    ) {
      return;
    }
    setIndustry(null);
    setOrganization(null);
    setBranch(null);
    setService(null);
    setSlot(null);
    resetEntryBranches();
    setEntryMode(ENTRY_SERVICE);
    lastDirectionRef.current = -1;
    setStep(STEP_INDUSTRY);
  }, [
    branchFromUrl?.id,
    orgFromUrl?.id,
    requestedStep,
    resetEntryBranches,
    selectedIndustryFromUrl?.id,
  ]);

  useEffect(() => {
    if (
      !selectedIndustryFromUrl?.id ||
      industry?.id === selectedIndustryFromUrl.id
    )
      return;
    const matchingIndustry = industries.find(
      (item) => item.id === selectedIndustryFromUrl.id,
    );
    setIndustry(
      matchingIndustry || {
        id: selectedIndustryFromUrl.id,
        name: selectedIndustryFromUrl.name,
        urlName: selectedIndustryFromUrl.urlName,
      },
    );
    setOrganization(null);
    setBranch(null);
    setService(null);
    setSlot(null);
    resetEntryBranches();
    setEntryMode(ENTRY_SERVICE);
    lastDirectionRef.current = 1;
    setStep(requestedStep === STEP_INDUSTRY ? STEP_INDUSTRY : STEP_ORG);
  }, [
    selectedIndustryFromUrl,
    industry?.id,
    industries,
    requestedStep,
    resetEntryBranches,
  ]);

  useEffect(() => {
    if (!orgFromUrl?.id || organization?.id === orgFromUrl.id) return;
    if (!selectedIndustryFromUrl?.id) setIndustry(null);
    setOrganization({
      id: orgFromUrl.id,
      name: orgFromUrl.name,
      urlName: orgFromUrl.urlName,
    });
    setBranch(null);
    setService(null);
    setSlot(null);
    resetEntryBranches();
    setStep(requestedStep ?? STEP_ORG);
  }, [
    orgFromUrl,
    organization?.id,
    requestedStep,
    resetEntryBranches,
    selectedIndustryFromUrl?.id,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (!branchFromUrl?.id || organization?.id) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const detail = await fetchBranchDetail(lang, branchFromUrl.id);

        if (cancelled) return;
        const nextBranch = detail || {
          id: branchFromUrl.id,
          name: branchFromUrl.name,
          urlName: branchFromUrl.urlName,
        };
        const nextOrg =
          detail?.organization ||
          detail?.organisation ||
          (detail?.organization_id || detail?.organizationId
            ? {
              id: detail.organization_id || detail.organizationId,
              name:
                detail.organizationName ||
                detail.organization_name ||
                searchParams.get("orgName") ||
                branchFromUrl.name,
            }
            : null);

        if (nextOrg?.id) {
          setOrganization(nextOrg);
        }
        setBranch(nextBranch);
        if (requestedCategoryFromUrl?.id) {
          setService({
            id: requestedCategoryFromUrl.id,
            category_id: requestedCategoryFromUrl.id,
            categoryId: requestedCategoryFromUrl.id,
            name: requestedCategoryFromUrl.name,
            urlName: requestedCategoryFromUrl.urlName,
          });
        }
        if (requestedWorkerFromUrl?.id) {
          setSelectedWorker({
            id: requestedWorkerFromUrl.id,
            name: requestedWorkerFromUrl.name,
            urlName: requestedWorkerFromUrl.urlName,
          });
        }
        setStep(requestedStep ?? STEP_TIME);
      } catch (err) {
        console.warn("[booking] branch deep-link skipped", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    branchFromUrl,
    lang,
    organization?.id,
    requestedCategoryFromUrl,
    requestedStep,
    requestedWorkerFromUrl,
    searchParams,
  ]);

  /* -------------------- Organizations (per-industry) -------------------- */
  const loadOrganizations = useCallback(
    async (industryId) => {
      if (!industryId) return;
      setOrganizationsLoading(true);
      setOrganizationsError(null);
      try {
        const data = await fetchOrganizations(lang, { industryId });
        setOrganizations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("[booking] organizations load failed", err);
        setOrganizationsError(err);
      } finally {
        setOrganizationsLoading(false);
      }
    },
    [lang],
  );

  useEffect(() => {
    if (industry?.id) loadOrganizations(industry.id);
  }, [industry?.id, loadOrganizations]);

  /* -------------------- Branches (org detail) -------------------- */
  const loadBranches = useCallback(
    async (organizationId) => {
      if (!organizationId) return;
      setBranchesLoading(true);
      setBranchesError(null);
      try {
        const data = await fetchOrganizationDetail(lang, organizationId);
        const detailOrganization = getOrganizationFromDetail(data);
        if (Number(detailOrganization?.id) === Number(organizationId)) {
          setOrganization((current) =>
            Number(current?.id) === Number(organizationId)
              ? {
                ...current,
                ...detailOrganization,
                urlName: detailOrganization.urlName || current.urlName,
              }
              : current,
          );
        }
        const list = Array.isArray(data?.branches)
          ? data.branches
          : Array.isArray(data)
            ? data
            : [];
        setBranches(list);
      } catch (err) {
        console.error("[booking] branches load failed", err);
        setBranchesError(err);
      } finally {
        setBranchesLoading(false);
      }
    },
    [lang],
  );

  useEffect(() => {
    if (organization?.id) loadBranches(organization.id);
  }, [organization?.id, loadBranches]);

  useEffect(() => {
    if (!branchFromUrl?.id || !organization?.id) return;
    const matchingBranch = branches.find(
      (item) => item.id === branchFromUrl.id,
    );
    if (branch?.id === branchFromUrl.id) {
      const currentBranch = matchingBranch || branch;
      if (
        matchingBranch &&
        Number(branch.display_type) !== Number(matchingBranch.display_type)
      ) {
        setBranch(matchingBranch);
      }
      const needsEntry = branchNeedsEntryChoice(currentBranch);
      setEntryMode(needsEntry ? requestedEntryMode : ENTRY_SERVICE);
      if (requestedCategoryFromUrl?.id) {
        const primaryService = getPrimaryService(service);
        const currentCategoryId =
          primaryService?.categoryId ?? getServiceCategoryId(primaryService);
        if (currentCategoryId !== requestedCategoryFromUrl.id) {
          setService({
            id: requestedCategoryFromUrl.id,
            category_id: requestedCategoryFromUrl.id,
            categoryId: requestedCategoryFromUrl.id,
            name: requestedCategoryFromUrl.name,
            urlName: requestedCategoryFromUrl.urlName,
          });
        }
      }
      if (needsEntry && requestedWorkerFromUrl?.id) {
        setSelectedWorker({
          id: requestedWorkerFromUrl.id,
          name: requestedWorkerFromUrl.name,
          urlName: requestedWorkerFromUrl.urlName,
        });
      }
      setCategoryWorkersOpen(
        Boolean(
          needsEntry &&
          requestedEntryMode === ENTRY_SERVICE &&
          requestedServiceSubStep === SERVICE_SUB_WORKERS &&
          (requestedCategoryFromUrl?.id || service),
        ),
      );
      setWorkerServicesOpen(
        Boolean(
          needsEntry &&
          requestedEntryMode === ENTRY_SPECIALIST &&
          requestedServiceSubStep === SERVICE_SUB_SERVICES &&
          requestedWorkerFromUrl?.id,
        ),
      );
      if (requestedStep != null) setStep(requestedStep);
      return;
    }
    const nextBranch = matchingBranch || {
      id: branchFromUrl.id,
      name: branchFromUrl.name,
      urlName: branchFromUrl.urlName,
    };
    const needsEntry = branchNeedsEntryChoice(nextBranch);
    const nextEntryMode = needsEntry ? requestedEntryMode : ENTRY_SERVICE;
    setBranch(nextBranch);
    setService(null);
    setSlot(null);
    resetEntryBranches();
    setEntryMode(nextEntryMode);
    if (requestedCategoryFromUrl?.id) {
      setService({
        id: requestedCategoryFromUrl.id,
        category_id: requestedCategoryFromUrl.id,
        categoryId: requestedCategoryFromUrl.id,
        name: requestedCategoryFromUrl.name,
        urlName: requestedCategoryFromUrl.urlName,
      });
    }
    if (needsEntry && requestedWorkerFromUrl?.id) {
      setSelectedWorker({
        id: requestedWorkerFromUrl.id,
        name: requestedWorkerFromUrl.name,
        urlName: requestedWorkerFromUrl.urlName,
      });
    }
    if (
      needsEntry &&
      nextEntryMode === ENTRY_SERVICE &&
      requestedServiceSubStep === SERVICE_SUB_WORKERS &&
      requestedCategoryFromUrl?.id
    ) {
      setCategoryWorkersOpen(true);
    }
    if (
      needsEntry &&
      nextEntryMode === ENTRY_SPECIALIST &&
      requestedServiceSubStep === SERVICE_SUB_SERVICES &&
      requestedWorkerFromUrl?.id
    ) {
      setWorkerServicesOpen(true);
    }
    setStep(requestedStep ?? STEP_SERVICE);
  }, [
    branchFromUrl,
    organization?.id,
    branches,
    branch,
    branch?.id,
    branch?.display_type,
    requestedEntryMode,
    requestedServiceSubStep,
    requestedCategoryFromUrl,
    requestedWorkerFromUrl,
    requestedStep,
    resetEntryBranches,
    service,
  ]);

  const advanceAfterBranch = useCallback(
    (nextBranch) => {
      setEntryMode(branchNeedsEntryChoice(nextBranch) ? null : ENTRY_SERVICE);
      resetEntryBranches();
      lastDirectionRef.current = 1;
      setStep(STEP_SERVICE);
    },
    [resetEntryBranches],
  );

  const loadWorkers = useCallback(
    async (branchId) => {
      if (!branchId) return;
      setWorkersLoading(true);
      setWorkersError(null);
      try {
        const data = await fetchBranchWorkers(lang, branchId);
        setWorkers(Array.isArray(data) ? data.map(normalizeWorker) : []);
      } catch (err) {
        console.error("[booking] branch workers load failed", err);
        setWorkersError(err);
      } finally {
        setWorkersLoading(false);
      }
    },
    [lang],
  );

  const handleRecommendedSlotSelect = useCallback((rawSlot) => {
    if (!rawSlot) return;

    setSlot({
      raw: rawSlot,
      date: rawSlot,
    });

    setTicketError(null);
    setTicketState("idle");
    setStep(STEP_TIME);
  }, []);

  const loadCategoryWorkers = useCallback(
    async ({ categoryId, branchId }) => {
      if (!categoryId || !branchId) return;
      setWorkersLoading(true);
      setWorkersError(null);
      try {
        const data = await fetchUsersByCategory(lang, { categoryId, branchId });
        setWorkers(Array.isArray(data) ? data.map(normalizeWorker) : []);
      } catch (err) {
        console.error("[booking] category workers load failed", err);
        setWorkersError(err);
      } finally {
        setWorkersLoading(false);
      }
    },
    [lang],
  );

  useEffect(() => {
    if (step !== STEP_SERVICE) return;
    if (entryMode !== ENTRY_SPECIALIST) return;
    if (workerServicesOpen) return;
    if (!branch?.id) return;
    loadWorkers(branch.id);
  }, [step, entryMode, workerServicesOpen, branch?.id, loadWorkers]);

  useEffect(() => {
    if (step !== STEP_SERVICE) return;
    if (entryMode !== ENTRY_SERVICE) return;
    if (!categoryWorkersOpen) return;
    if (!branch?.id || !service) return;
    const primaryService = getPrimaryService(service);
    const categoryId =
      primaryService?.categoryId ?? getServiceCategoryId(primaryService);
    loadCategoryWorkers({ categoryId, branchId: branch.id });
  }, [
    step,
    entryMode,
    categoryWorkersOpen,
    branch?.id,
    service,
    loadCategoryWorkers,
  ]);

  useEffect(() => {
    if (!selectedWorker?.id || workers.length === 0) return;
    if (hasProfessions(selectedWorker)) return;
    const matchingWorker = workers.find(
      (worker) => Number(worker?.id) === Number(selectedWorker.id),
    );
    if (!hasProfessions(matchingWorker)) return;

    setSelectedWorker((current) => {
      if (!current || Number(current.id) !== Number(matchingWorker.id)) return current;
      if (hasProfessions(current)) return current;
      return normalizeWorker({
        ...current,
        ...matchingWorker,
        urlName: current.urlName || matchingWorker.urlName,
      });
    });
  }, [selectedWorker, workers]);

  /* -------------------- User (only when authenticated) -------------------- */
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setUser(null);
      setUserLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setUserLoading(true);
    (async () => {
      try {
        const apiLocale = getSecondApiLocale(lang);
        const res = await SecondApi.get(`/api/${apiLocale}/auth/getUserInfo`);
        if (cancelled) return;
        const u =
          res?.data?.user || res?.data?.data?.user || res?.data?.data || null;
        const contact = getUserContact(u);
        setUser(u);
        if (contact.email) setEmail((prev) => prev || contact.email);
        if (contact.name) setClientName((prev) => prev || contact.name);
        if (contact.phone) setPhone((prev) => prev || contact.phone);
      } catch (err) {
        // Non-fatal: anonymous mode still works.
        console.warn("[booking] user info skipped", err);
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally only re-fetch on auth/lang change — not on email/name
    // edits, otherwise the user's edits would be reset every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, lang]);

  /* -------------------- Step transitions -------------------- */
  const syncUrlForStep = useCallback(
    (targetStep, { replace = true } = {}) => {
      const stepKey = STEP_URL_KEYS[targetStep];

      if (targetStep === STEP_INDUSTRY) {
        if (industry?.id) {
          syncIndustryUrl(industry, {
            replace,
            stepKey,
            preserveScroll: false,
          });
        } else {
          navigate(withBookingQuery(bookingBasePath, { stepKey }), {
            replace,
            preventScrollReset: true,
          });
        }
        return;
      }

      if (targetStep === STEP_ORG) {
        if (organization?.id) {
          syncBookingUrl(organization, null, {
            replace,
            stepKey,
            preserveScroll: false,
          });
        } else if (industry?.id) {
          syncIndustryUrl(industry, { replace, preserveScroll: false });
        } else {
          navigate(bookingBasePath, {
            replace,
            preventScrollReset: true,
          });
        }
        return;
      }

      if (targetStep === STEP_SERVICE) {
        if (organization?.id) {
          const needsEntry = branchNeedsEntryChoice(branch);
          const primaryService = getPrimaryService(service);
          const isMultiService = hasMultipleSelectedServices(service);
          const categoryId =
            primaryService?.categoryId ?? getServiceCategoryId(primaryService);
          const serviceToken = categoryId
            ? {
              ...primaryService,
              id: categoryId,
              name: primaryService?.name || primaryService?.translate?.name,
            }
            : null;
          syncBookingUrl(organization, branch || null, {
            replace,
            stepKey: needsEntry && !entryMode ? "entry" : "service",
            mode: needsEntry && entryMode ? entryMode : null,
            subStep:
              needsEntry && entryMode === ENTRY_SERVICE && categoryWorkersOpen
                ? SERVICE_SUB_WORKERS
                : needsEntry &&
                  entryMode === ENTRY_SPECIALIST &&
                  workerServicesOpen
                ? SERVICE_SUB_SERVICES
                  : null,
            categoryItem:
              !isMultiService &&
              needsEntry &&
              entryMode === ENTRY_SERVICE &&
              categoryWorkersOpen
                ? serviceToken
                : null,
            workerItem:
              needsEntry && entryMode === ENTRY_SPECIALIST && workerServicesOpen
                ? selectedWorker
                : null,
            preserveScroll: false,
          });
        }
        return;
      }

      if (targetStep === STEP_TIME || targetStep === STEP_CONFIRM) {
        if (organization?.id) {
          const needsEntry = branchNeedsEntryChoice(branch);
          const primaryService = getPrimaryService(service);
          const isMultiService = hasMultipleSelectedServices(service);
          const categoryId =
            primaryService?.categoryId ?? getServiceCategoryId(primaryService);
          const serviceToken = categoryId
            ? {
              ...primaryService,
              id: categoryId,
              name: primaryService?.name || primaryService?.translate?.name,
            }
            : null;
          syncBookingUrl(organization, branch || null, {
            replace,
            stepKey,
            mode: needsEntry ? entryMode : null,
            categoryItem: needsEntry && !isMultiService ? serviceToken : null,
            workerItem: needsEntry ? selectedWorker : null,
            preserveScroll: false,
          });
        }
      }
    },
    [
      bookingBasePath,
      branch,
      categoryWorkersOpen,
      entryMode,
      industry,
      navigate,
      organization,
      selectedWorker,
      service,
      syncBookingUrl,
      syncIndustryUrl,
      workerServicesOpen,
    ],
  );

  const goNext = () => {
    lastDirectionRef.current = 1;
    setStep((s) => {
      const nextStep = Math.min(s + 1, TOTAL_STEPS - 1);
      syncUrlForStep(nextStep);
      return nextStep;
    });
  };
  const goBack = () => {
    lastDirectionRef.current = -1;
    if (
      step === STEP_SERVICE &&
      branchNeedsEntryChoice(branch) &&
      workerServicesOpen
    ) {
      setWorkerServicesOpen(false);
      setService(null);
      setSlot(null);
      if (organization?.id) {
        syncBookingUrl(organization, branch || null, {
          replace: true,
          stepKey: "service",
          mode: ENTRY_SPECIALIST,
          preserveScroll: false,
        });
      }
      return;
    }
    if (
      step === STEP_SERVICE &&
      branchNeedsEntryChoice(branch) &&
      categoryWorkersOpen
    ) {
      setCategoryWorkersOpen(false);
      setSelectedWorker(null);
      setSlot(null);
      if (organization?.id) {
        syncBookingUrl(organization, branch || null, {
          replace: true,
          stepKey: "service",
          mode: ENTRY_SERVICE,
          preserveScroll: false,
        });
      }
      return;
    }
    if (step === STEP_SERVICE && branchNeedsEntryChoice(branch) && entryMode) {
      setEntryMode(null);
      setService(null);
      setSlot(null);
      resetEntryBranches();
      if (organization?.id) {
        syncBookingUrl(organization, branch || null, {
          replace: true,
          stepKey: "entry",
          preserveScroll: false,
        });
      }
      return;
    }
    const nextStep = Math.max(step - 1, 0);
    syncUrlForStep(nextStep);
    setStep(nextStep);
  };
  const goTo = (idx) => {
    // Only allow jumping back to a completed step (not skipping forward).
    if (idx <= step) {
      lastDirectionRef.current = idx > step ? 1 : -1;
      syncUrlForStep(idx);
      setStep(idx);
    }
  };

  /*
   * Auto-skip the branch step when the chosen organization has exactly one
   * branch — the user has nothing to choose between, so we make the
   * selection for them and silently advance to the next/previous step
   * (preserving the direction they were already navigating).
   */
  const resetForNewBooking = () => {
    setIndustry(null);
    setOrganization(null);
    setBranch(null);
    setService(null);
    setSlot(null);
    setEntryMode(ENTRY_SERVICE);
    resetEntryBranches();
    if (!isAuthenticated) {
      setEmail("");
      setClientName("");
      setPhone("");
    }
    setClientComment("");
    setTicket(null);
    setTicketError(null);
    setTicketState("idle");
    setShowSuccessModal(false);
    setStep(STEP_INDUSTRY);
    navigate(bookingBasePath, { replace: true, preventScrollReset: true });
  };

  const handleIndustrySelect = (it) => {
    setIndustry(it);
    syncIndustryUrl(it, { stepKey: "organization", preserveScroll: false });
    if (it?.id !== industry?.id) {
      // reset downstream
      setOrganization(null);
      setBranch(null);
      setService(null);
      setSlot(null);
      setEntryMode(ENTRY_SERVICE);
      resetEntryBranches();
    }
    lastDirectionRef.current = 1;
    setStep(STEP_ORG);
  };

  const handleOrganizationSelect = (org) => {
    setOrganization(org);
    syncBookingUrl(org);
    const orgBranches = Array.isArray(org?.branches)
      ? org.branches
      : Array.isArray(org?.branch)
        ? org.branch
        : [];
    if (org?.id !== organization?.id) {
      setBranch(orgBranches.length === 1 ? orgBranches[0] : null);
      setService(null);
      setSlot(null);
      setEntryMode(ENTRY_SERVICE);
      resetEntryBranches();
    }
    lastDirectionRef.current = 1;
    setStep(STEP_ORG);
  };

  const handleOrganizationStepNext = () => {
    if (!branch) return;
    if (organization) {
      syncBookingUrl(organization, branch, {
        replace: true,
        preserveScroll: false,
      });
    }
    advanceAfterBranch(branch);
  };

  const handleServiceSelect = (svc, preselectedSlot = null) => {
    const serviceSlot = selectedWorker?.id && entryMode === ENTRY_SPECIALIST
      ? preselectedSlot
      : null;
    setService(svc);
    setSlot(serviceSlot);
    if (serviceSlot) {
      lastDirectionRef.current = 1;
      setStep(STEP_TIME);
    }
    if (entryMode === ENTRY_SERVICE) {
      setSelectedWorker(null);
      setCategoryWorkersOpen(false);
    }
    if (organization?.id && branchNeedsEntryChoice(branch)) {
      const primaryService = getPrimaryService(svc);
      const isMultiService = hasMultipleSelectedServices(svc);
      const categoryId =
        primaryService?.categoryId ?? getServiceCategoryId(primaryService);
      const serviceToken = categoryId
        ? {
          ...primaryService,
          id: categoryId,
          name: primaryService?.name || primaryService?.translate?.name,
        }
        : primaryService;
      syncBookingUrl(organization, branch || null, {
        replace: true,
        stepKey: serviceSlot ? "time" : "service",
        mode: entryMode || ENTRY_SERVICE,
        subStep: workerServicesOpen ? SERVICE_SUB_SERVICES : null,
        categoryItem: isMultiService ? null : serviceToken,
        workerItem: entryMode === ENTRY_SPECIALIST ? selectedWorker : null,
      });
    }
  };

  /**
   * Commit path: the "Ընտրել" / "Տեսնել ևս N մասնաճյուղ" buttons on the org
   * card. They both pick the org AND advance — no second click on the
   * wizard footer's "Շարունակել" needed. The header click stays as a
   * "soft select" so users can browse multiple cards' selection state
   * before committing.
   */
  /**
   * Shortcut path: when the org card surfaces a clickable branch row, the
   * user picks org + branch in one click and jumps straight to the next
   * service/entry screen.
   */
  const handleOrgAndBranchShortcut = (org, br) => {
    if (org?.id !== organization?.id) {
      setOrganization(org);
    }
    if (br?.id !== branch?.id) {
      setBranch(br);
    }
    syncBookingUrl(org, br, { preserveScroll: false });
    setService(null);
    setSlot(null);
    setEntryMode(ENTRY_SERVICE);
    resetEntryBranches();
    lastDirectionRef.current = 1;
    advanceAfterBranch(br);
  };

  const handleChooseEntryService = () => {
    setEntryMode(ENTRY_SERVICE);
    setService(null);
    setSlot(null);
    resetEntryBranches();
    if (organization?.id) {
      syncBookingUrl(organization, branch || null, {
        replace: true,
        stepKey: "service",
        mode: ENTRY_SERVICE,
        preserveScroll: false,
      });
    } else {
      syncUrlForStep(STEP_SERVICE);
    }
  };

  const handleChooseEntrySpecialist = () => {
    setEntryMode(ENTRY_SPECIALIST);
    setService(null);
    setSlot(null);
    resetEntryBranches();
    if (organization?.id) {
      syncBookingUrl(organization, branch || null, {
        replace: true,
        stepKey: "service",
        mode: ENTRY_SPECIALIST,
        preserveScroll: false,
      });
    } else {
      syncUrlForStep(STEP_SERVICE);
    }
  };

  const handleWorkerSelect = (worker, workerSlot = null) => {
    const shouldAdvanceToTime =
      !workerSlot && categoryWorkersOpen && entryMode === ENTRY_SERVICE && Boolean(service);
    setSelectedWorker(worker);
    setSlot(workerSlot || null);
    if (entryMode === ENTRY_SPECIALIST) {
      setService(null);
      setWorkerServicesOpen(false);
    }
    if (organization?.id && branchNeedsEntryChoice(branch)) {
      const primaryService = getPrimaryService(service);
      const isMultiService = hasMultipleSelectedServices(service);
      const categoryId =
        primaryService?.categoryId ?? getServiceCategoryId(primaryService);
      const serviceToken = categoryId
        ? {
          ...primaryService,
          id: categoryId,
          name: primaryService?.name || primaryService?.translate?.name,
        }
        : null;
      syncBookingUrl(organization, branch || null, {
        replace: true,
        stepKey: shouldAdvanceToTime ? "time" : "service",
        mode: entryMode || ENTRY_SPECIALIST,
        subStep: shouldAdvanceToTime
          ? null
          : categoryWorkersOpen
            ? SERVICE_SUB_WORKERS
            : null,
        categoryItem: isMultiService ? null : serviceToken,
        workerItem: worker,
        preserveScroll: shouldAdvanceToTime ? false : true,
      });
    }
    if (shouldAdvanceToTime) {
      lastDirectionRef.current = 1;
      setStep(STEP_TIME);
    }
  };

  const handleWorkerContinueServices = () => {
    if (!selectedWorker) return;
    setService(null);
    setSlot(null);
    setWorkerServicesOpen(true);
    if (organization?.id) {
      syncBookingUrl(organization, branch || null, {
        replace: true,
        stepKey: "service",
        mode: ENTRY_SPECIALIST,
        subStep: SERVICE_SUB_SERVICES,
        workerItem: selectedWorker,
        preserveScroll: false,
      });
    }
  };

  const handleServiceContinueWorkers = () => {
    if (!service) return;
    const primaryService = getPrimaryService(service);
    const isMultiService = hasMultipleSelectedServices(service);
    const categoryId =
      primaryService?.categoryId ?? getServiceCategoryId(primaryService);
    const serviceToken = categoryId
      ? {
        ...primaryService,
        id: categoryId,
        name: primaryService?.name || primaryService?.translate?.name,
      }
      : primaryService;
    setSelectedWorker(null);
    setSlot(null);
    setCategoryWorkersOpen(true);
    if (organization?.id) {
      syncBookingUrl(organization, branch || null, {
        replace: true,
        stepKey: "service",
        mode: ENTRY_SERVICE,
        subStep: SERVICE_SUB_WORKERS,
        categoryItem: isMultiService ? null : serviceToken,
        preserveScroll: false,
      });
    }
  };

  const handleCategoryWorkerContinue = () => {
    if (!selectedWorker) return;
    goNext();
  };

  const loadSelectedWorkerCategories = useCallback(
    async (activeLang) => {
      if (!selectedWorker?.id) return [];
      return fetchCategoriesByUser(activeLang, selectedWorker.id);
    },
    [selectedWorker?.id],
  );

  const handleSubmitWorkerRating = useCallback(
    async ({ rating, comment }) => {
      const orderTicketId =
        ticket?.order_ticket_id ??
        ticket?.orderTicketId ??
        ticket?.ticket_id ??
        ticket?.id;
      if (!orderTicketId) {
        throw new Error(t("booking.feedback.afterBooking"));
      }
      return setWorkerRating(lang, { orderTicketId, rating, comment });
    },
    [lang, t, ticket],
  );

  const handleFeedbackLoginRequired = useCallback(() => {
    setLoginModalTitle?.(t("booking.feedback.loginRequiredTitle"));
    setOpenLoginModal?.(true);
  }, [setLoginModalTitle, setOpenLoginModal, t]);

  /* -------------------- Ticket creation -------------------- */
  const submitTicket = useCallback(
    async (overrideReqDate = null) => {
      if (!branch || !service || (!slot && !overrideReqDate)) return;

      setTicketState("creating");
      setTicketError(null);
      setStep(STEP_CONFIRM);

      try {
        const categoryIds = getSelectedCategoryIds(service);
        const categoryId = categoryIds[0];
        if (!categoryId) {
          throw new Error(t("booking.service.emptyChildren"));
        }

        const result = await createTicket(lang, {
          branchId: branch.id,
          categoryId,
          categoryIds,
          reqDate: overrideReqDate || slot.raw || slot.date,
          clientName: effectiveClientName || null,
          phone: effectivePhone,
          email: effectiveEmail || null,
          clientComment: clientComment || null,
          userId: selectedWorker?.id || null,
        });

        if (result?.isBlocked && result?.errorMessage === "recommended") {
          const primaryService = getPrimaryService(service);
          const suggestionsResponse = await fetchTimeSuggestions(lang, {
            branchId: branch.id,
            categoryId,
            categoryIds,
            userId: selectedWorker?.id || getServiceUserId(primaryService),
          });

          setTicketError({
            type: "recommended",
            message:
              "Ընտրված ժամը հասանելի չէ։ Խնդրում ենք ընտրել առաջարկվող ժամերից մեկը։",
            suggestions: Array.isArray(suggestionsResponse?.data)
              ? suggestionsResponse.data
              : [],
          });

          setTicketState("error");
          return;
        }

        const blockingErrors = [
          "break",
          "user_on_business_trip",
          "not_working_time",
          "not_working_window",
        ];

        if (blockingErrors.includes(result?.errorMessage)) {
          setTicketError({
            type: "simple",
            message: result?.value || "Ամրագրումը հնարավոր չեղավ իրականացնել",
          });

          setTicketState("error");
          return;
        }

        setTicket(result?.item);
        setTicketState("success");
        setShowSuccessModal(true);
      } catch (err) {
        setTicketError({
          type: "simple",
          message:
            err?.payload?.message ||
            err?.response?.data?.message ||
            err?.message ||
            "Ամրագրումը հնարավոր չեղավ իրականացնել",
          statusCode: err?.statusCode || err?.response?.status,
          originalError: err,
        });

        setTicketState("error");
      }
    },
    [
      branch,
      service,
      slot,
      lang,
      effectiveClientName,
      effectivePhone,
      effectiveEmail,
      clientComment,
      selectedWorker?.id,
      t,
    ],
  );

  // Convenience: jump to ticket creation when step 5 confirms
  const handleTimeStepNext = () => {
    submitTicket();
  };

  const completedSteps = useMemo(() => {
    const list = [];
    const needsEntry = branchNeedsEntryChoice(branch);
    if (industry) list.push(0);
    if (organization && branch) list.push(1);
    if (needsEntry) {
      if (entryMode) list.push(2);
      if (entryMode === ENTRY_SPECIALIST) {
        if (selectedWorker) list.push(3);
        if (service) list.push(4);
      } else if (entryMode === ENTRY_SERVICE) {
        if (service) list.push(3);
        if (selectedWorker) list.push(4);
      }
      if (slot) list.push(5);
      return list;
    }
    if (service) list.push(2);
    if (slot) list.push(3);
    return list;
  }, [industry, organization, branch, entryMode, selectedWorker, service, slot]);

  const progressSteps = useMemo(() => {
    if (!branchNeedsEntryChoice(branch)) return DEFAULT_PROGRESS_STEPS;
    return entryMode === ENTRY_SERVICE
      ? ENTRY_SERVICE_FIRST_PROGRESS_STEPS
      : ENTRY_PROGRESS_STEPS;
  }, [branch, entryMode]);

  const currentProgressStep = useMemo(() => {
    if (!branchNeedsEntryChoice(branch)) return step;
    if (step <= STEP_ORG) return step;
    if (step === STEP_SERVICE) {
      if (!entryMode) return 2;
      if (entryMode === ENTRY_SPECIALIST) return workerServicesOpen ? 4 : 3;
      if (entryMode === ENTRY_SERVICE) return categoryWorkersOpen ? 4 : 3;
      return 3;
    }
    if (step === STEP_TIME) return 5;
    if (step === STEP_CONFIRM) return 6;
    return step;
  }, [branch, categoryWorkersOpen, entryMode, step, workerServicesOpen]);

  const bookingViewKey = useMemo(() => {
    if (step !== STEP_SERVICE || !branchNeedsEntryChoice(branch)) {
      return String(step);
    }
    if (!entryMode) return "entry";
    if (entryMode === ENTRY_SPECIALIST) {
      return workerServicesOpen ? "specialist-services" : "specialist";
    }
    if (entryMode === ENTRY_SERVICE) {
      return categoryWorkersOpen ? "service-workers" : "service";
    }
    return `service-${entryMode}`;
  }, [branch, categoryWorkersOpen, entryMode, step, workerServicesOpen]);

  const goToProgressStep = (idx) => {
    const needsEntry = branchNeedsEntryChoice(branch);
    if (!needsEntry) {
      goTo(idx);
      return;
    }
    if (idx > currentProgressStep) return;
    lastDirectionRef.current = idx > currentProgressStep ? 1 : -1;
    if (idx <= STEP_ORG) {
      syncUrlForStep(idx);
      setStep(idx);
      return;
    }
    if (idx === 2) {
      setEntryMode(null);
      setService(null);
      setSlot(null);
      setSelectedWorker(null);
      if (organization?.id) {
        syncBookingUrl(organization, branch || null, {
          replace: true,
          stepKey: "entry",
          preserveScroll: false,
        });
      }
      setStep(STEP_SERVICE);
      return;
    }
    if (idx === 3 || idx === 4) {
      if (entryMode === ENTRY_SPECIALIST) {
        setWorkerServicesOpen(idx === 4);
        setCategoryWorkersOpen(false);
      }
      if (entryMode === ENTRY_SERVICE) {
        setCategoryWorkersOpen(idx === 4);
        setWorkerServicesOpen(false);
      }
      syncUrlForStep(STEP_SERVICE);
      setStep(STEP_SERVICE);
      return;
    }
    const realStep = idx === 5 ? STEP_TIME : STEP_CONFIRM;
    syncUrlForStep(realStep);
    setStep(realStep);
  };

  const organizationStepItems = useMemo(() => {
    if (organizations.length > 0) return organizations;
    return organization ? [organization] : [];
  }, [organizations, organization]);

  const ticketsHref = useMemo(() => {
    return lang === "en"
      ? "/en/online-booking/tickets"
      : "/online-booking/tickets";
  }, [lang]);

  const handleRegisterPrompt = useCallback(() => {
    if (typeof setOpenRegisterModal === "function") {
      setOpenRegisterModal(true);
    }
  }, [setOpenRegisterModal]);

  const isHeroCompact = step !== STEP_INDUSTRY;

  useEffect(() => {
    if (!didMountScrollRef.current) {
      didMountScrollRef.current = true;
      return;
    }
    const target = bookingScrollTargetRef.current;
    if (!target) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    window.requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    });
  }, [bookingViewKey]);

  return (
    <div className={`booking-page${step === STEP_CONFIRM ? " booking-page--confirm" : ""}`}>
      <div
        className={`booking-page-hero${isHeroCompact ? " booking-page-hero--compact" : ""}`}
        style={{
          background: "var(--main-gradient)",
          padding: isHeroCompact
            ? "0"
            : "clamp(40px, 7vw, 80px) 0 clamp(70px, 12vw, 120px) 0",
          minHeight: isHeroCompact ? 80 : undefined,
          position: "relative",
          zIndex: 0,
          fontFamily: BOOKING_FONT_FAMILY,
        }}
      >
        {!isHeroCompact ? (
          <div className="booking-page-hero__inner container container-two">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  className="fw-700 mb-2"
                  style={{
                    fontFamily: BOOKING_FONT_FAMILY,
                    color: "#fff",
                    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
                    lineHeight: 1.15,
                  }}
                >
                  {t("booking.page.title")}
                </h1>
                <p
                  className="mb-0"
                  style={{
                    color: "rgba(255, 255, 255, 0.85)",
                    fontSize: "clamp(0.95rem, 1.5vw, 1.05rem)",
                    maxWidth: 720,
                  }}
                >
                  {t("booking.page.subtitle")}
                </p>
              </div>
              <Link
                to={ticketsHref}
                className="booking-page-tickets-link pill fw-600"
                style={{
                  background: "#ffffff",
                  border: "none",
                  padding: "10px 22px",
                  textDecoration: "none",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
                }}
              >
                <span style={{ color: "#0f172a", fontWeight: 600 }}>
                  <i
                    className="las la-ticket-alt me-2"
                    style={{ color: "hsl(166 100% 32%)" }}
                    aria-hidden="true"
                  />
                  {t("booking.page.myTickets")}
                </span>
              </Link>
            </div>
          </div>
        ) : null}
      </div>

      <section
        className="booking-page-section"
        style={{
          paddingBottom: "clamp(60px, 10vw, 140px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div className="container container-two">
          <div
            ref={bookingScrollTargetRef}
            className="booking-progress-shell"
            style={{
              marginTop: isHeroCompact ? "-40px" : "-60px",
              background: palette.surface,
              border: palette.border,
              borderRadius: 24,
              padding: "20px clamp(16px, 3vw, 28px) 10px clamp(16px, 3vw, 28px)",
              boxShadow: palette.shadow,
            }}
          >
            <BookingProgress
              palette={palette}
              steps={progressSteps}
              current={currentProgressStep}
              completed={completedSteps}
              onStepClick={goToProgressStep}
            />
          </div>

          <div className="booking-step-host" style={{ marginTop: 16 }}>
            {step === STEP_INDUSTRY && (
              <IndustryStep
                palette={palette}
                lang={lang}
                industries={industries}
                loading={industriesLoading}
                error={industriesError}
                selectedId={industry?.id}
                onSelect={handleIndustrySelect}
                onRetry={loadIndustries}
              />
            )}
            {step === STEP_ORG && (
              <OrganizationStep
                palette={palette}
                lang={lang}
                industry={industry}
                organizations={organizationStepItems}
                loading={organizationsLoading}
                error={organizationsError}
                selectedId={organization?.id}
                onSelect={handleOrganizationSelect}
                onSelectBranchShortcut={handleOrgAndBranchShortcut}
                onBack={goBack}
                onNext={handleOrganizationStepNext}
                canContinue={Boolean(branch?.id)}
                onRetry={() => loadOrganizations(industry?.id)}
              />
            )}
            {step === STEP_SERVICE &&
              branchNeedsEntryChoice(branch) &&
              !entryMode && (
                <BookingEntryChoiceStep
                  palette={palette}
                  lang={lang}
                  industry={industry}
                  organization={organization}
                  branch={branch}
                  onChooseSpecialist={handleChooseEntrySpecialist}
                  onChooseService={handleChooseEntryService}
                  onBack={goBack}
                />
              )}
            {step === STEP_SERVICE &&
              branchNeedsEntryChoice(branch) &&
              entryMode === ENTRY_SPECIALIST &&
              !workerServicesOpen && (
                <SpecialistSelectionStep
                  palette={palette}
                  lang={lang}
                  industry={industry}
                  organization={organization}
                  branch={branch}
                  service={service}
                  workers={workers}
                  selectedWorker={selectedWorker}
                  selectedSlot={slot}
                  orderTicketId={
                    ticket?.order_ticket_id ??
                    ticket?.orderTicketId ??
                    ticket?.ticket_id ??
                    ticket?.id
                  }
                  isAuthenticated={isAuthenticated}
                  loading={workersLoading}
                  error={workersError}
                  onSelect={handleWorkerSelect}
                  onBack={goBack}
                  onRetry={() => loadWorkers(branch?.id)}
                  continueDisabled={!selectedWorker}
                  onContinueServices={handleWorkerContinueServices}
                  onSubmitRating={handleSubmitWorkerRating}
                  onLoginRequired={handleFeedbackLoginRequired}
                />
              )}
            {step === STEP_SERVICE &&
              branchNeedsEntryChoice(branch) &&
              entryMode === ENTRY_SPECIALIST &&
              workerServicesOpen && (
                <ServiceStep
                  palette={palette}
                  lang={lang}
                  industry={industry}
                  organization={organization}
                  branch={branch}
                  selectedWorker={selectedWorker}
                  selectedService={service}
                  orderTicketId={
                    ticket?.order_ticket_id ??
                    ticket?.orderTicketId ??
                    ticket?.ticket_id ??
                    ticket?.id
                  }
                  isAuthenticated={isAuthenticated}
                  loadCategories={loadSelectedWorkerCategories}
                  autoAdvance={false}
                  allowMultiple={isMultiBookingEnabled(branch)}
                  onSelect={handleServiceSelect}
                  onBack={goBack}
                  onNext={goNext}
                  onSubmitRating={handleSubmitWorkerRating}
                  onLoginRequired={handleFeedbackLoginRequired}
                />
              )}
            {step === STEP_SERVICE &&
              branchNeedsEntryChoice(branch) &&
              entryMode === ENTRY_SERVICE &&
              !categoryWorkersOpen && (
                <ServiceStep
                  palette={palette}
                  lang={lang}
                  industry={industry}
                  organization={organization}
                  branch={branch}
                  selectedService={service}
                  orderTicketId={
                    ticket?.order_ticket_id ??
                    ticket?.orderTicketId ??
                    ticket?.ticket_id ??
                    ticket?.id
                  }
                  isAuthenticated={isAuthenticated}
                  autoAdvance={false}
                  allowMultiple={isMultiBookingEnabled(branch)}
                  onSelect={handleServiceSelect}
                  onBack={goBack}
                  onNext={handleServiceContinueWorkers}
                  onSubmitRating={handleSubmitWorkerRating}
                  onLoginRequired={handleFeedbackLoginRequired}
                />
              )}
            {step === STEP_SERVICE &&
              branchNeedsEntryChoice(branch) &&
              entryMode === ENTRY_SERVICE &&
              categoryWorkersOpen && (
                <SpecialistSelectionStep
                  palette={palette}
                  lang={lang}
                  industry={industry}
                  organization={organization}
                  branch={branch}
                  service={service}
                  workers={workers}
                  selectedWorker={selectedWorker}
                  selectedSlot={slot}
                  orderTicketId={
                    ticket?.order_ticket_id ??
                    ticket?.orderTicketId ??
                    ticket?.ticket_id ??
                    ticket?.id
                  }
                  isAuthenticated={isAuthenticated}
                  loading={workersLoading}
                  error={workersError}
                  title={t("booking.specialists.byServiceTitle")}
                  subtitle={t("booking.specialists.byServiceSubtitle")}
                  continueLabel={t("booking.common.next")}
                  continueDisabled={!selectedWorker}
                  onSelect={handleWorkerSelect}
                  onBack={goBack}
                  onRetry={() => {
                    const primaryService = getPrimaryService(service);
                    const categoryId =
                      primaryService?.categoryId ??
                      getServiceCategoryId(primaryService);
                    loadCategoryWorkers({ categoryId, branchId: branch?.id });
                  }}
                  onContinueServices={handleCategoryWorkerContinue}
                  onSubmitRating={handleSubmitWorkerRating}
                  onLoginRequired={handleFeedbackLoginRequired}
                />
              )}
            {step === STEP_SERVICE && !branchNeedsEntryChoice(branch) && (
              <ServiceStep
                palette={palette}
                lang={lang}
                industry={industry}
                organization={organization}
                branch={branch}
                selectedService={service}
                orderTicketId={
                  ticket?.order_ticket_id ??
                  ticket?.orderTicketId ??
                  ticket?.ticket_id ??
                  ticket?.id
                }
                isAuthenticated={isAuthenticated}
                autoAdvance={false}
                allowMultiple={isMultiBookingEnabled(branch)}
                onSelect={handleServiceSelect}
                onBack={goBack}
                onNext={goNext}
                onSubmitRating={handleSubmitWorkerRating}
                onLoginRequired={handleFeedbackLoginRequired}
              />
            )}
            {step === STEP_TIME && (
              <TimeSlotStep
                palette={palette}
                lang={lang}
                branch={branch}
                service={service}
                selectedWorker={selectedWorker}
                selectedWorkerProfession={selectedWorkerProfession}
                user={user}
                isAuthenticated={isAuthenticated}
                userLoading={userLoading}
                accountContact={accountContact}
                selectedSlot={slot}
                onSelectSlot={setSlot}
                email={email}
                onEmailChange={setEmail}
                clientName={clientName}
                onClientNameChange={setClientName}
                phone={phone}
                onPhoneChange={setPhone}
                clientComment={clientComment}
                onClientCommentChange={setClientComment}
                onBack={goBack}
                onNext={handleTimeStepNext}
              />
            )}
            {step === STEP_CONFIRM && (
              <ConfirmStep
                palette={palette}
                lang={lang}
                state={ticketState}
                ticket={ticket}
                error={ticketError}
                industry={industry}
                organization={organization}
                branch={branch}
                service={service}
                slot={slot}
                email={effectiveEmail}
                clientName={effectiveClientName}
                clientComment={clientComment}
                isAuthenticated={isAuthenticated}
                onRetry={submitTicket}
                onNewBooking={resetForNewBooking}
                onReopenSuccess={() => setShowSuccessModal(true)}
                ticketsHref={ticketsHref}
                onRegister={handleRegisterPrompt}
                onRecommendedSlotSelect={handleRecommendedSlotSelect}
                onRecommendedRetry={submitTicket}
              />
            )}
          </div>
        </div>
      </section>

      <style>
        {`
          .booking-mobile-stepper {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .booking-mobile-stepper::-webkit-scrollbar {
            display: none;
          }

          .booking-page,
          .booking-page :where(div, span, p, a, button, input, textarea, label, small, strong, em, h1, h2, h3, h4, h5, h6) {
            font-family: "Segoe UI", "Noto Sans Armenian", "Noto Sans", Arial, sans-serif !important;
            letter-spacing: 0 !important;
          }

          .booking-progress-shell {
            scroll-margin-top: 12px;
          }

          body.booking-hide-site-footer .footer-section,
          body.booking-hide-site-footer .bottom-footer,
          body.booking-hide-site-footer .footer-two,
          body.booking-hide-site-footer .bottom-footer-two {
            display: none !important;
          }

          @media (max-width: 575px) {
            .booking-page-hero {
              padding: 28px 0 84px 0 !important;
            }

            .booking-page-hero--compact {
              min-height: 80px !important;
              padding: 0 !important;
            }

            .booking-page-hero__inner,
            .booking-page-section > .container {
              padding-left: 12px;
              padding-right: 12px;
            }

            .booking-page-hero h1 {
              font-size: 1.55rem !important;
              line-height: 1.15 !important;
              margin-bottom: 6px !important;
            }

            .booking-page-hero p {
              font-size: 0.88rem !important;
              line-height: 1.45 !important;
              max-width: 100% !important;
            }

            .booking-page-tickets-link {
              width: 100%;
              justify-content: center;
              min-height: 44px;
              padding: 10px 16px !important;
            }

            .booking-page-section {
              padding-bottom: 220px !important;
            }

            .booking-progress-shell {
              margin-top: -50px !important;
              border-radius: 18px !important;
              padding: 8px 10px 9px 10px !important;
            
            }

            .booking-page-hero--compact + .booking-page-section .booking-progress-shell {
              margin-top: -40px !important;
            }

            .booking-step-host {
              margin-top: 12px !important;
            }

            .booking-step-card {
              border-radius: 18px !important;
            }

            .booking-step-header {
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 10px !important;
            }

            .booking-step-header__copy {
              width: 100% !important;
              flex: 0 0 auto !important;
              min-width: 100% !important;
            }

            .booking-step-header__accessory {
              width: 100%;
              display: flex;
              justify-content: flex-start;
            }

            .booking-step-header__accessory .badge {
              max-width: 100%;
              white-space: normal !important;
              text-align: left;
              line-height: 1.25;
            }

            .booking-organization-hint {
              flex-direction: column !important;
              align-items: stretch !important;
            }

            .booking-organization-hint > span:first-child {
              width: 32px !important;
              height: 32px !important;
            }

            .booking-step-card input,
            .booking-step-card textarea,
            .booking-step-card button,
            .booking-step-card a {
              font-family: "Segoe UI", "Noto Sans Armenian", "Noto Sans", Arial, sans-serif !important;
            }

            .booking-step-footer {
              position: fixed !important;
              left: 10px;
              right: 10px;
              bottom:0px;
              z-index: 1085;
              gap: 10px !important;
              align-items: stretch !important;
              border-radius: 18px;
              border: 1px solid rgba(255, 255, 255, 0.12);
              background: ${palette.surface} !important;
              backdrop-filter: none;
              padding: 12px !important;
              padding-bottom: 12px !important;
              box-shadow: 0 -18px 36px rgba(0, 0, 0, 0.38);
            }

            .booking-page ~ div[class*="scroll"],
            .booking-page button[class*="scroll"],
            .booking-page .scroll-to-top {
              bottom: calc(120px + env(safe-area-inset-bottom, 0px)) !important;
              z-index: 1084 !important;
            }

            body.booking-confirm-step .scroll-to-top {
              bottom: 140px !important;
              z-index: 999999 !important;
            }

            body.booking-confirm-step .scroll-to-top svg {
              fill: hsl(var(--main)) !important;
            }

            .booking-step-footer > * {
              width: 100%;
              min-height: 46px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              white-space: nowrap;
              padding-left: 12px !important;
              padding-right: 12px !important;
            }
          }

          @media (max-width: 374px) {
            .booking-step-footer {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>

      <TicketSuccessModal
        isOpen={showSuccessModal && ticketState === "success"}
        onClose={() => setShowSuccessModal(false)}
        palette={palette}
        lang={lang}
        ticket={ticket}
        organization={organization}
        branch={branch}
        service={service}
        slot={slot}
        isAuthenticated={isAuthenticated}
        ticketsHref={ticketsHref}
        onNewBooking={resetForNewBooking}
        onRegister={handleRegisterPrompt}
        holderName={effectiveClientName}
        onCancel={async () => {
          const orderId = ticket?.id || ticket?.order_ticket_id || ticket?.orderTicketId;
          if (!orderId) throw new Error(t("booking.common.errorGeneric"));
          await cancelTicket(lang, orderId);
          setShowSuccessModal(false);
          resetForNewBooking();
        }}
      />
    </div>
  );
};

export const BeautyBookingDemo = () => <SalonBookingPage />;

export default BookingPage;
